import {
  DeleteOfferFromSystem,
  DeleteOfferFromSystemVariables,
} from "_graphql/mutation/__generated__/DeleteOfferFromSystem";
import {
  GetEndorsementParticipant,
  GetEndorsementParticipantVariables,
} from "_graphql/queries/__generated__/GetEndorsementParticipant";
import {
  GetKEKReinsurerOffers,
  GetKEKReinsurerOffersVariables,
} from "_graphql/kek/queries/__generated__/GetKEKReinsurerOffers";
import {
  GetNotAcceptedParticipants,
  GetNotAcceptedParticipantsVariables,
} from "_graphql/queries/__generated__/GetNotAcceptedParticipants";
import {
  GetOfferAssociates,
  GetOfferAssociatesVariables,
} from "_graphql/queries/__generated__/GetOfferAssociates";
import {
  GetOfferAuditTrail,
  GetOfferAuditTrailVariables,
} from "_graphql/visal/queries/__generated__/GetOfferAuditTrail";
import {
  GetOfferParticipant,
  GetOfferParticipantVariables,
} from "_graphql/visal/queries/__generated__/GetOfferParticipant";
import {
  GetOfferSet,
  GetOfferSetVariables,
} from "_graphql/kek/queries/__generated__/GetOfferSet";
import {
  GetReinsurerOffers,
  GetReinsurerOffersVariables,
} from "_graphql/queries/__generated__/GetReinsurerOffers";
import {
  KEKOffer,
  KEKOfferVariables,
} from "_graphql/kek/queries/__generated__/KEKOffer";
import {
  Offer,
  OfferVariables,
} from "_graphql/visal/queries/__generated__/Offer";
import {
  OfferFleet,
  OfferFleetVariables,
} from "_graphql/queries/__generated__/OfferFleet";
import {
  RemoveReInsurerFromList,
  RemoveReInsurerFromListVariables,
} from "_graphql/mutation/__generated__/RemoveReInsurerFromList";
import {
  ReopenOffer,
  ReopenOfferVariables,
} from "_graphql/mutation/__generated__/ReopenOffer";
import {
  deleteOffer,
  reOpenOffer,
  removeReInsurerFromParticipation,
} from "_graphql/visal/mutations/offers";
import {
  getCancelledOffer,
  getCancelledOffers,
  getEndorsementParticipant,
  getOffer,
  getOfferAssociates,
  getOfferAuditTrail,
  getOfferFleet,
  getOfferParticipant,
  getOffers,
  getReInsurerOffers,
  getUnacceptedOfferParticipants,
} from "_graphql/visal/queries/offers";
import {
  getKEKOffer,
  getKEKOfferParticipant,
  getKEKOffers,
  getKEKReinsurerOffers,
  getOfferSet,
} from "_graphql/kek/queries/offers.kek";
import { useMutation, useQuery } from "@apollo/client";

import { KEKOffers } from "_graphql/kek/queries/__generated__/KEKOffers";
import { KEKOffersVariables } from "_graphql/queries/kek/__generated__/KEKOffers";
import {
  Offers,
  OffersVariables,
} from "_graphql/visal/queries/__generated__/Offers";
import {
  CancelledOffers,
  CancelledOffersVariables,
} from "_graphql/visal/queries/__generated__/CancelledOffers";
import {
  GetCancelledOffer,
  GetCancelledOfferVariables,
} from "_graphql/visal/queries/__generated__/GetCancelledOffer";

export type iVariables = {
  page: number;
  pageSize: number;
  search: string;
};

export const useOffers = (variables: OffersVariables & iVariables) => {
  const { page, pageSize, ...offerVariables } = variables;
  const start = (page - 1) * pageSize;
  const { data, ...rest } = useQuery<Offers, OffersVariables>(getOffers, {
    variables: {
      args: {
        ...offerVariables.args,
        search: offerVariables.search,
        offset: start,
        limit: pageSize,
      },
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: "network-only",
  });

  const offers = data?.rows || [];
  const count = data?.count || 0;

  return { offers, count, ...rest };
};

export const useKEKOffers = (variables: KEKOffersVariables) => {
  const { data, ...rest } = useQuery<KEKOffers, KEKOffersVariables>(
    getKEKOffers,
    {
      variables: variables,
      notifyOnNetworkStatusChange: true,
      fetchPolicy: "network-only",
    }
  );

  const offers = data?.rows || [];
  const count = data?.count || 0;

  return { offers, count, ...rest };
};

export const useKEKBankingTrayOffers = (variables: GetOfferSetVariables) => {
  const { data, ...rest } = useQuery<GetOfferSet, GetOfferSetVariables>(
    getOfferSet,
    {
      variables: variables,
      notifyOnNetworkStatusChange: true,
      fetchPolicy: "network-only",
    }
  );

  const offers = data?.rows || [];
  const count = data?.count || 0;

  return { offers, count, ...rest };
};

export const useCancelledOffers = (variables: CancelledOffersVariables) => {
  const { data, ...rest } = useQuery<CancelledOffers, CancelledOffersVariables>(
    getCancelledOffers,
    {
      variables,
      notifyOnNetworkStatusChange: true,
      fetchPolicy: "network-only",
    }
  );

  const offers = data?.rows || [];
  const count = data?.count || 0;

  return { offers, count, ...rest };
};

export const useCancelledOffer = (variables: GetCancelledOfferVariables) => {
  const { data, ...rest } = useQuery<
    GetCancelledOffer,
    GetCancelledOfferVariables
  >(getCancelledOffer, {
    variables,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: "network-only",
  });

  const offer = data?.getCancelledOffer;

  return { offer, ...rest };
};

export const useReInsurerOffers = (
  variables: GetReinsurerOffersVariables & iVariables
) => {
  const { page, pageSize, search, ...offerVariables } = variables;
  const start = (page - 1) * pageSize;
  const { data, ...rest } = useQuery<
    GetReinsurerOffers,
    GetReinsurerOffersVariables
  >(getReInsurerOffers, {
    variables: {
      args: {
        ...offerVariables.args,
        search,
        offset: start,
        limit: pageSize,
      },
      reinsurer_id: offerVariables.args?.reinsurer_id || "",
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: "network-only",
  });

  return {
    offers: data?.getReinsurerOffers || [],
    count: data?.getReinsurerOffersCount,
    ...rest,
  };
};
export const useKEKReInsurerOffers = (
  variables: GetKEKReinsurerOffersVariables & iVariables
) => {
  const { page, pageSize, search, ...offerVariables } = variables;
  const start = (page - 1) * pageSize;
  const { data, ...rest } = useQuery<
    GetKEKReinsurerOffers,
    GetKEKReinsurerOffersVariables
  >(getKEKReinsurerOffers, {
    variables: {
      args: {
        ...offerVariables.args,
        search,
        offset: start,
        limit: pageSize,
      },
      reinsurer_id: offerVariables.args?.reinsurer_id || "",
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: "network-only",
  });

  return {
    offers: data?.getReinsurerOffers || [],
    count: data?.getReinsurerOffersCount,
    ...rest,
  };
};

export const useOffer = (variables: OfferVariables) => {
  const { data, ...rest } = useQuery<Offer, OfferVariables>(getOffer, {
    variables,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: "network-only",
  });
  return {
    offer: data?.findSingleOffer,
    ...rest,
  };
};

export const useKEKOffer = (variables: KEKOfferVariables) => {
  const { data, ...rest } = useQuery<KEKOffer, KEKOfferVariables>(getKEKOffer, {
    variables,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: "network-only",
  });
  return {
    offer: data?.findSingleOffer,
    ...rest,
  };
};

export const useOfferUnAcceptedParticipants = (
  variables: GetNotAcceptedParticipantsVariables
) => {
  const { data, ...rest } = useQuery<
    GetNotAcceptedParticipants,
    GetNotAcceptedParticipantsVariables
  >(getUnacceptedOfferParticipants, {
    variables,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: "network-only",
  });
  return {
    unAcceptedPariticipants: data?.getNotAcceptedParticipants || [],
    ...rest,
  };
};

export const useOfferFleet = (variables: OfferFleetVariables) => {
  const { data, ...rest } = useQuery<OfferFleet, OfferFleetVariables>(
    getOfferFleet,
    {
      variables,
      notifyOnNetworkStatusChange: true,
      fetchPolicy: "cache-first",
    }
  );
  return {
    offerFleet: data?.getOfferFleet || [],
    ...rest,
  };
};

export const useOfferAuditTrail = (variables: GetOfferAuditTrailVariables) => {
  const { data, ...rest } = useQuery<
    GetOfferAuditTrail,
    GetOfferAuditTrailVariables
  >(getOfferAuditTrail, {
    variables,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: "network-only",
  });
  return {
    offerHistory: data?.getOfferAuditTrail || [],
    ...rest,
  };
};

export const useKEKOfferParticipant = (
  variables: GetOfferParticipantVariables
) => {
  const { data, ...rest } = useQuery<
    GetOfferParticipant,
    GetOfferParticipantVariables
  >(getKEKOfferParticipant, {
    variables,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: "network-only",
  });
  return {
    participant: data?.getOfferParticipant,
    ...rest,
  };
};

export const useOfferParticipant = (
  variables: GetOfferParticipantVariables
) => {
  const { data, ...rest } = useQuery<
    GetOfferParticipant,
    GetOfferParticipantVariables
  >(getOfferParticipant, {
    variables,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: "network-only",
  });
  return {
    participant: data?.getOfferParticipant,
    ...rest,
  };
};
export const useEndorsementParticipant = (
  variables: GetEndorsementParticipantVariables
) => {
  const { data, ...rest } = useQuery<
    GetEndorsementParticipant,
    GetEndorsementParticipantVariables
  >(getEndorsementParticipant, {
    variables,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: "network-only",
  });
  return {
    participant: data?.getEndorsementParticipant,
    ...rest,
  };
};
export const useOfferParticipantAssociates = (
  variables: GetOfferAssociatesVariables
) => {
  const { data, ...rest } = useQuery<
    GetOfferAssociates,
    GetOfferAssociatesVariables
  >(getOfferAssociates, {
    variables,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: "network-only",
  });
  return {
    associates: data?.getOfferAssociates || [],
    ...rest,
  };
};

export const useRemoveParticipantFromParticipation = () => {
  const [removeParticipant, rest] = useMutation<
    RemoveReInsurerFromList,
    RemoveReInsurerFromListVariables
  >(removeReInsurerFromParticipation, {
    refetchQueries: ["Offer"],
  });
  return {
    removeParticipant,
    ...rest,
  };
};
export const useRemoveOffer = () => {
  const [removeOffer, rest] = useMutation<
    DeleteOfferFromSystem,
    DeleteOfferFromSystemVariables
  >(deleteOffer, {
    refetchQueries: ["Offers"],
  });
  return {
    removeOffer,
    ...rest,
  };
};
export const useReOpenOffer = () => {
  const [reopen, rest] = useMutation<ReopenOffer, ReopenOfferVariables>(
    reOpenOffer,
    {
      refetchQueries: ["Offers"],
    }
  );
  return {
    reopen,
    ...rest,
  };
};
